import { useMemo } from 'react';
import { useGetList } from 'react-admin';
import { MediaLike } from '../utils';
import { extractIdFromURI } from '../../../utils/resources/ids';

interface Params {
    mediasIds: string[] | undefined;
    /** Page size of the request, for callers asking for more medias than the default page holds. */
    perPage?: number;
}

interface Output {
    images: MediaLike[] | undefined;
    audios: MediaLike[] | undefined;
    documents: MediaLike[] | undefined;
    isLoading: boolean;
    error: any;
    refetch: () => void;
}

// Compose FM download URL using env var as base (aligned with platform-v2 behavior)
const composeMediaFmDownloadUrl = (mediaId: string, token: string) =>
    `${process.env.REACT_APP_FILE_SYSTEM_MAPPER_ENTRYPOINT}/smartConnectView/file_system_mappers/${mediaId}/media/download.json?bearer=${token}`;

// Updated version to use react-admin useGetList with correct v3 signature
const useGetMedias = ({ mediasIds, perPage }: Params): Output => {
    // We can't call hooks conditionally; compute safeIds and use the enabled option
    const safeIds = Array.isArray(mediasIds) ? mediasIds : [];

    // RA v3 useGetList signature: (resource, pagination?, sort?, filter?, options?)
    const { data: dataMap, ids, loading, error, refetch }: any = useGetList(
        'media',
        perPage ? { page: 1, perPage } : undefined,
        undefined,
        { id: safeIds },
        { enabled: safeIds.length > 0 }
    );

    // Convert RecordMap + ids to array for processing
    const dataArray: any[] | undefined = useMemo(() => {
        if (!ids || ids.length === 0) return undefined;
        return ids.map((id: any) => (dataMap as any)[id]).filter(Boolean);
    }, [ids, dataMap]);

    const enrichedData: MediaLike[] | undefined = useMemo(() => {
        if (!Array.isArray(dataArray)) return undefined;
        return dataArray.map((item: any) => {
            const mediaId = extractIdFromURI(item.id || item['@id'] || '');
            const token = item?.contentUrlTokenAccess || item?.token || '';
            const urlToDownload = mediaId && token ? composeMediaFmDownloadUrl(mediaId, token) : undefined;
            return { ...item, urlToDownload } as MediaLike;
        });
    }, [dataArray]);

    const { images, audios, documents } = useMemo(() => {
        if (!enrichedData) return { images: undefined, audios: undefined, documents: undefined };
        const images: MediaLike[] = [];
        const audios: MediaLike[] = [];
        const documents: MediaLike[] = [];
        enrichedData.forEach((item: any) => {
            const type = item?.['@type'] || item?.type || item?.mimeType || '';
            if (typeof type === 'string' && (type === 'Image' || type.includes('image'))) {
                images.push(item);
            } else if (typeof type === 'string' && (type === 'Audio' || type.includes('audio'))) {
                audios.push(item);
            } else {
                documents.push(item);
            }
        });
        return { images, audios, documents };
    }, [enrichedData]);

    return {
        images,
        audios,
        documents,
        isLoading: loading,
        error,
        refetch,
    };
};

export default useGetMedias;
